import { Component, Input, Output, EventEmitter, OnInit, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { LucideAngularModule, X, Bell, Smartphone, Send, Loader2 } from 'lucide-angular';
import { DevicesService } from '../../../../core/services/devices.service';
import {
  NotificationsService,
  NotificationRequest,
} from '../../../../core/services/notifications.service';
import {
  ErrorModalComponent,
  ErrorDetails,
} from '../../../../shared/components/error-modal/error-modal.component';

export interface Device {
  id: string;
  name: string;
  device_id: string;
  is_active: boolean;
  position_id?: string;
}

@Component({
  selector: 'app-notification-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, LucideAngularModule, ErrorModalComponent],
  template: `
    <div class="modal-overlay" (click)="closeModal()">
      <div class="modal-content" (click)="$event.stopPropagation()">
        <div class="modal-header">
          <h2>Enviar Notificação - {{ positionName }}</h2>
          <button class="close-btn" (click)="closeModal()">
            <lucide-icon [img]="XIcon" class="w-5 h-5"></lucide-icon>
          </button>
        </div>

        <div class="modal-body">
          <div class="position-info">
            <h3>{{ positionName }}</h3>
            <p>Selecione os dispositivos para enviar a notificação</p>
          </div>

          <div class="devices-section">
            <div class="section-header">
              <h4>Dispositivos Disponíveis</h4>
              <div class="select-actions" *ngIf="!preSelectedDeviceId">
                <button class="select-all-btn" (click)="selectAll()">Selecionar Todos</button>
                <button class="deselect-all-btn" (click)="deselectAll()">Desmarcar Todos</button>
              </div>
            </div>

            <div class="devices-list" *ngIf="!loadingDevices">
              <div
                *ngFor="let device of availableDevices"
                class="device-item"
                [class.selected]="selectedDevices.includes(device.id)"
              >
                <label class="device-checkbox">
                  <input
                    type="checkbox"
                    [checked]="selectedDevices.includes(device.id)"
                    [disabled]="preSelectedDeviceId === device.id"
                    (change)="toggleDevice(device.id)"
                  />
                  <span
                    class="checkmark"
                    [class.disabled]="preSelectedDeviceId === device.id"
                  ></span>
                </label>
                <div class="device-info">
                  <lucide-icon [img]="SmartphoneIcon" [size]="16" class="device-icon"></lucide-icon>
                  <div class="device-details">
                    <div class="device-name">
                      {{ device.name || 'Dispositivo ' + device.device_id }}
                    </div>
                    <div class="device-id">Posto: {{ positionName }}</div>
                  </div>
                </div>
                <div class="device-status" [class]="device.is_active ? 'active' : 'inactive'">
                  {{ device.is_active ? 'activo' : 'inactivo' }}
                </div>
              </div>
            </div>

            <div *ngIf="loadingDevices" class="loading-state">
              <lucide-icon [img]="Loader2Icon" [size]="20" class="animate-spin"></lucide-icon>
              <span>Carregando dispositivos...</span>
            </div>

            <div *ngIf="!loadingDevices && availableDevices.length === 0" class="empty-state">
              <lucide-icon [img]="SmartphoneIcon" [size]="32" class="empty-icon"></lucide-icon>
              <p>Nenhum dispositivo disponível para este posto</p>
            </div>
          </div>

          <div class="message-section">
            <label for="notificationMessage">Mensagem da Notificação</label>
            <textarea
              id="notificationMessage"
              [(ngModel)]="notificationMessage"
              placeholder="Digite sua mensagem aqui..."
              rows="3"
              maxlength="500"
            >
            </textarea>
            <div class="char-count">{{ notificationMessage.length }}/500</div>
          </div>
        </div>

        <div class="modal-footer">
          <button class="cancel-btn" (click)="closeModal()">Cancelar</button>
          <button
            class="send-btn"
            (click)="sendNotification()"
            [disabled]="selectedDevices.length === 0 || !notificationMessage.trim() || sending"
          >
            <lucide-icon
              *ngIf="sending"
              [img]="Loader2Icon"
              [size]="16"
              class="animate-spin"
            ></lucide-icon>
            <lucide-icon *ngIf="!sending" [img]="SendIcon" [size]="16"></lucide-icon>
            {{ sending ? 'Enviando...' : 'Enviar Notificação' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Modal de Erro -->
    <app-error-modal *ngIf="showErrorModal" [error]="errorDetails" (close)="closeErrorModal()">
    </app-error-modal>
  `,
  styles: [
    `
      .modal-overlay {
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background-color: rgb(17 24 39 / 0.5);
        display: flex;
        justify-content: center;
        align-items: center;
        z-index: 1000;
      }

      .modal-content {
        background: white;
        border-radius: 8px;
        width: 90%;
        max-width: 600px;
        max-height: 80vh;
        overflow: hidden;
        box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
      }

      .modal-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 1rem 1.5rem;
        border-bottom: 1px solid #e5e7eb;
        background-color: #f9fafb;
      }

      .modal-header h2 {
        margin: 0;
        color: #1f2937;
        font-size: 1.25rem;
        font-weight: 600;
      }

      .close-btn {
        background: none;
        border: none;
        font-size: 1.25rem;
        color: #6b7280;
        cursor: pointer;
        padding: 0.25rem;
        border-radius: 0.25rem;
        transition: all 0.2s;
      }

      .close-btn:hover {
        color: #374151;
        background-color: #f3f4f6;
      }

      .modal-body {
        padding: 1.5rem;
        overflow-y: auto;
        max-height: calc(80vh - 140px);
      }

      .position-info {
        margin-bottom: 24px;
        padding: 16px;
        background: #f3f4f6;
        border-radius: 8px;
        border: 1px solid #e5e7eb;
      }

      .position-info h3 {
        margin: 0 0 4px 0;
        color: #111827;
        font-size: 1rem;
        font-weight: 600;
      }

      .position-info p {
        margin: 0;
        color: #6b7280;
        font-size: 0.875rem;
      }

      .devices-section {
        margin-bottom: 24px;
      }

      .section-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 16px;
      }

      .section-header h4 {
        margin: 0;
        color: #111827;
        font-size: 0.875rem;
        font-weight: 600;
      }

      .select-actions {
        display: flex;
        gap: 8px;
      }

      .select-all-btn,
      .deselect-all-btn {
        padding: 4px 8px;
        border: 1px solid #d1d5db;
        background: #ffffff;
        color: #374151;
        border-radius: 4px;
        font-size: 0.75rem;
        cursor: pointer;
        transition: all 0.2s;
      }

      .select-all-btn:hover,
      .deselect-all-btn:hover {
        background: #f3f4f6;
        border-color: #9ca3af;
      }

      .devices-list {
        max-height: 200px;
        overflow-y: auto;
        border: 1px solid #e5e7eb;
        border-radius: 8px;
        background: #ffffff;
      }

      .device-item {
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 12px 16px;
        border-bottom: 1px solid #f3f4f6;
        transition: background-color 0.2s;
      }

      .device-item:last-child {
        border-bottom: none;
      }

      .device-item:hover {
        background: #f9fafb;
      }

      .device-item.selected {
        background: #eff6ff;
        border-color: #3b82f6;
      }

      .device-checkbox {
        display: flex;
        align-items: center;
        cursor: pointer;
      }

      .device-checkbox input[type='checkbox'] {
        display: none;
      }

      .checkmark {
        width: 18px;
        height: 18px;
        border: 2px solid #d1d5db;
        border-radius: 4px;
        position: relative;
        transition: all 0.2s;
      }

      .device-checkbox input[type='checkbox']:checked + .checkmark {
        background: #3b82f6;
        border-color: #3b82f6;
      }

      .device-checkbox input[type='checkbox']:checked + .checkmark::after {
        content: '';
        position: absolute;
        left: 5px;
        top: 2px;
        width: 4px;
        height: 8px;
        border: solid white;
        border-width: 0 2px 2px 0;
        transform: rotate(45deg);
      }

      .device-checkbox input[type='checkbox']:disabled + .checkmark.disabled {
        opacity: 0.6;
        cursor: not-allowed;
      }

      .device-info {
        display: flex;
        align-items: center;
        gap: 8px;
        flex: 1;
      }

      .device-icon {
        color: #6b7280;
      }

      .device-details {
        flex: 1;
      }

      .device-name {
        color: #111827;
        font-weight: 500;
        font-size: 0.875rem;
      }

      .device-id {
        color: #6b7280;
        font-size: 0.75rem;
        margin-top: 2px;
      }

      .device-status {
        padding: 2px 8px;
        border-radius: 12px;
        font-size: 0.75rem;
        font-weight: 500;
      }

      .device-status.active {
        background: #dcfce7;
        color: #166534;
      }

      .device-status.inactive {
        background: #fef2f2;
        color: #dc2626;
      }

      .loading-state,
      .empty-state {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        padding: 32px 16px;
        color: #6b7280;
        text-align: center;
      }

      .empty-icon {
        margin-bottom: 8px;
        opacity: 0.6;
      }

      .message-section {
        margin-bottom: 16px;
      }

      .message-section label {
        display: block;
        margin-bottom: 8px;
        color: #374151;
        font-weight: 500;
        font-size: 0.875rem;
      }

      .message-section textarea {
        width: 100%;
        padding: 12px;
        border: 1px solid #d1d5db;
        border-radius: 8px;
        background: #ffffff;
        color: #111827;
        font-size: 0.875rem;
        resize: vertical;
        min-height: 80px;
        transition: border-color 0.2s;
      }

      .message-section textarea:focus {
        outline: none;
        border-color: #3b82f6;
        box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
      }

      .char-count {
        text-align: right;
        color: #6b7280;
        font-size: 0.75rem;
        margin-top: 4px;
      }

      .modal-footer {
        display: flex;
        justify-content: flex-end;
        gap: 0.75rem;
        padding: 1rem 1.5rem;
        border-top: 1px solid #e5e7eb;
        background-color: #f9fafb;
      }

      .cancel-btn {
        padding: 0.5rem 1rem;
        border: 1px solid #d1d5db;
        background: #ffffff;
        color: #374151;
        border-radius: 0.375rem;
        font-weight: 500;
        cursor: pointer;
        transition: all 0.2s;
      }

      .cancel-btn:hover {
        background: #f3f4f6;
        border-color: #9ca3af;
      }

      .send-btn {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        padding: 0.5rem 1rem;
        border: none;
        background: #1f2937;
        color: #ffffff;
        border-radius: 0.375rem;
        font-weight: 500;
        cursor: pointer;
        transition: all 0.2s;
      }

      .send-btn:hover:not(:disabled) {
        background: #111827;
      }

      .send-btn:disabled {
        opacity: 0.6;
        cursor: not-allowed;
      }

      @keyframes spin {
        from {
          transform: rotate(0deg);
        }
        to {
          transform: rotate(360deg);
        }
      }

      .animate-spin {
        animation: spin 1s linear infinite;
      }
    `,
  ],
})
export class NotificationModalComponent implements OnInit {
  @Input() positionId!: string;
  @Input() positionName!: string;
  @Input() preSelectedDeviceId?: string; // Device específico já selecionado
  @Output() close = new EventEmitter<void>();
  @Output() notificationSent = new EventEmitter<{ devices: string[]; message: string }>();

  availableDevices: Device[] = [];
  selectedDevices: string[] = [];
  notificationMessage = '';
  loadingDevices = false;
  sending = false;
  showErrorModal = false;
  errorDetails: ErrorDetails | null = null;

  // Icons
  readonly BellIcon = Bell;
  readonly XIcon = X;
  readonly SmartphoneIcon = Smartphone;
  readonly SendIcon = Send;
  readonly Loader2Icon = Loader2;

  constructor(
    private devicesService: DevicesService,
    private notificationsService: NotificationsService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.loadDevices();
  }

  loadDevices(): void {
    this.loadingDevices = true;
    this.cdr.detectChanges(); // Forçar atualização para mostrar loading

    // Usar filtros da API em vez de filtrar no frontend
    const filters = {
      position_id: this.positionId,
      active_only: true,
    };

    console.log('Carregando dispositivos para posição:', this.positionId);
    console.log('Device pré-selecionado:', this.preSelectedDeviceId);

    this.devicesService.getAll(filters).subscribe({
      next: (response: any) => {
        console.log('Resposta completa da API:', response);

        // Extrair dados da estrutura de resposta
        let devices = [];
        if (Array.isArray(response)) {
          devices = response;
        } else if (response?.data?.data) {
          devices = response.data.data;
        } else if (response?.data) {
          devices = Array.isArray(response.data) ? response.data : [];
        }

        console.log('Dispositivos extraídos (raw):', devices);

        // Mapear para garantir que temos o campo 'name' correto
        // A API pode retornar 'device_name' em vez de 'name'
        this.availableDevices = devices.map((device: any) => ({
          ...device,
          name: device.name || device.device_name || `Dispositivo ${device.device_id}`,
        }));

        console.log('Dispositivos mapeados:', this.availableDevices);

        // Se existe um device pré-selecionado, selecionar automaticamente
        if (this.preSelectedDeviceId) {
          this.selectedDevices = [this.preSelectedDeviceId];
          console.log('Device pré-selecionado:', this.preSelectedDeviceId);
        }

        this.loadingDevices = false;
        this.cdr.detectChanges(); // Forçar atualização após carregar
      },
      error: (error) => {
        console.error('Erro ao carregar dispositivos:', error);
        this.availableDevices = [];
        this.loadingDevices = false;
        this.cdr.detectChanges(); // Forçar atualização em caso de erro
      },
    });
  }

  toggleDevice(deviceId: string): void {
    const index = this.selectedDevices.indexOf(deviceId);
    if (index > -1) {
      this.selectedDevices.splice(index, 1);
    } else {
      this.selectedDevices.push(deviceId);
    }
    this.cdr.detectChanges(); // Forçar atualização da UI
  }

  selectAll(): void {
    this.selectedDevices = this.availableDevices.map((device) => device.id);
    this.cdr.detectChanges(); // Forçar atualização da UI
  }

  deselectAll(): void {
    this.selectedDevices = [];
    this.cdr.detectChanges(); // Forçar atualização da UI
  }

  sendNotification(): void {
    if (this.selectedDevices.length === 0 || !this.notificationMessage.trim()) {
      return;
    }

    this.sending = true;
    this.cdr.detectChanges(); // Forçar atualização para mostrar spinner de envio

    // Enviar notificação para cada dispositivo selecionado
    const notifications = this.selectedDevices.map((deviceId) => {
      const device = this.availableDevices.find((d) => d.id === deviceId);
      const notification: NotificationRequest = {
        device_id: device?.device_id || deviceId,
        title: 'Notificação do Posto',
        body: this.notificationMessage,
        url: 'https://smvweb.entretech.co.mz',
        inserted_id: 0,
        data: {
          alert_type: 'position_notification',
          priority: 'normal',
          position_id: this.positionId,
        },
      };
      return this.notificationsService.sendNotification(notification);
    });

    // Enviar todas as notificações
    let completed = 0;
    let hasError = false;

    notifications.forEach((obs) => {
      obs.subscribe({
        next: () => {
          completed++;
          if (completed === notifications.length) {
            this.sending = false;
            this.cdr.detectChanges(); // Forçar atualização
            this.notificationSent.emit({
              devices: this.selectedDevices,
              message: this.notificationMessage,
            });
            this.closeModal();
          }
        },
        error: (error) => {
          console.error('Erro ao enviar notificação:', error);
          hasError = true;
          completed++;

          // Tratar erro específico
          this.handleNotificationError(error);

          if (completed === notifications.length) {
            this.sending = false;
            this.cdr.detectChanges(); // Forçar atualização
            if (!hasError) {
              // Só emitir sucesso se não houve erros
              this.notificationSent.emit({
                devices: this.selectedDevices,
                message: this.notificationMessage,
              });
              this.closeModal();
            }
          }
        },
      });
    });
  }

  handleNotificationError(error: any): void {
    // Extrair detalhes do erro da resposta HTTP
    const errorResponse = error?.error || error;

    this.errorDetails = {
      statusCode: errorResponse?.statusCode || error?.status || 500,
      message: errorResponse?.message || error?.message || 'Erro desconhecido',
      path: errorResponse?.path || '/notifications/send',
      method: errorResponse?.method || 'POST',
      timestamp: errorResponse?.timestamp || new Date().toISOString(),
      error: errorResponse?.error || 'HttpException',
    };

    this.showErrorModal = true;
    this.cdr.detectChanges(); // Forçar atualização para mostrar modal de erro
  }

  closeErrorModal(): void {
    this.showErrorModal = false;
    this.errorDetails = null;
    this.cdr.detectChanges(); // Forçar atualização
  }

  closeModal(): void {
    this.close.emit();
  }
}
